import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Space,
  Table, Tooltip, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import {
  attachTeamToStage, createTeam, detachTeamFromStage,
  getCompetitionProjects, getStageTeams, getTeams, updateStageTeam, updateTeam,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';

const { Text } = Typography;

/**
 * İki katman:
 *  - Takım (yarışma seviyesinde): ad, proje, robot — İzmir'den Roma'ya aynı
 *  - Etaba katılım: kategori ve sonuç — etaptan etaba değişiyor
 */
const StageTeamsTab = ({ stage }) => {
  const competitionId = stage.competition_id;
  const [stageTeams, setStageTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamModal, setTeamModal] = useState({ open: false, record: null });
  const [attachOpen, setAttachOpen] = useState(false);
  const [form] = Form.useForm();
  const [attachForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: st }, { data: t }] = await Promise.all([
        getStageTeams(stage.id),
        getTeams(competitionId),
      ]);
      setStageTeams(st.data || []);
      setAllTeams(t.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Takımlar yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id, competitionId]);

  useEffect(() => {
    load();
    getCompetitionProjects({ per_page: 100, is_active: true })
      .then(({ data }) => setProjects(data.data || []))
      .catch(() => setProjects([]));
  }, [load]);

  const submitTeam = async () => {
    try {
      const v = await form.validateFields();
      if (teamModal.record) {
        await updateTeam(teamModal.record.id, v);
        message.success('Takım güncellendi.');
      } else {
        const { data } = await createTeam(competitionId, v);
        // Yeni takim otomatik olarak bu etaba da eklensin - kullanici zaten
        // bu etabin ekraninda, ayrica "etaba ekle" demek zorunda kalmasin.
        await attachTeamToStage(stage.id, { competition_team_id: data.id });
        message.success('Takım oluşturuldu ve bu etaba eklendi.');
      }
      setTeamModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  const submitAttach = async () => {
    try {
      const v = await attachForm.validateFields();
      await attachTeamToStage(stage.id, v);
      message.success('Takım etaba eklendi.');
      setAttachOpen(false);
      attachForm.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.warning(apiErrorMessage(e));
    }
  };

  const attachedIds = stageTeams.map((st) => st.competition_team_id);
  const attachable = allTeams.filter((t) => !attachedIds.includes(t.id));

  const columns = [
    { title: 'Takım', dataIndex: ['team', 'name'], render: (v) => v || '—' },
    { title: 'Proje', dataIndex: ['team', 'project', 'name'], render: (v) => v || '—' },
    { title: 'Robot', dataIndex: ['team', 'robot_name'], render: (v) => v || '—' },
    {
      title: 'Robot Maliyeti',
      dataIndex: ['team', 'robot_cost'],
      width: 130,
      render: (v) => (v ? `${Number(v).toLocaleString('tr-TR')} ₺` : '—'),
    },
    {
      title: <Tooltip title="Bu etapta hangi kategoride yarışıyor — etaptan etaba değişebilir">Kategori</Tooltip>,
      dataIndex: 'category',
      width: 200,
      render: (v, r) => (
        <Input
          size="small"
          defaultValue={v}
          placeholder="Yeşil Girişimcilik…"
          onBlur={async (e) => {
            const val = e.target.value;
            if (val === (v || '')) return;
            try {
              await updateStageTeam(r.id, { category: val });
              message.success('Kategori güncellendi.');
              load();
            } catch (err) {
              message.error(apiErrorMessage(err));
            }
          }}
        />
      ),
    },
    {
      title: <Tooltip title="Geçti / elendi / derece — bir üst etaba kimi taşıyacağınızı gösterir">Sonuç</Tooltip>,
      dataIndex: 'result',
      width: 160,
      render: (v, r) => (
        <Input
          size="small"
          defaultValue={v}
          placeholder="geçti / elendi…"
          onBlur={async (e) => {
            const val = e.target.value;
            if (val === (v || '')) return;
            try {
              await updateStageTeam(r.id, { result: val });
              message.success('Sonuç kaydedildi.');
              load();
            } catch (err) {
              message.error(apiErrorMessage(err));
            }
          }}
        />
      ),
    },
    { title: 'Öğrenci', dataIndex: ['team', 'participants_count'], width: 80, render: (v) => v ?? 0 },
    {
      title: 'İşlemler',
      width: 110,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Tooltip title="Takım bilgilerini düzenle">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setTeamModal({ open: true, record: r.team });
                form.setFieldsValue(r.team);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Takım bu etaptan çıkarılsın mı?"
            description="Takımın kendisi silinmez, sadece bu etaptan çıkar."
            onConfirm={async () => {
              try {
                await detachTeamFromStage(r.id);
                message.success('Çıkarıldı.');
                load();
              } catch (e) {
                message.error(apiErrorMessage(e));
              }
            }}
            okText="Çıkar"
            cancelText="Vazgeç"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary">
        Takım yarışma seviyesinde tanımlanır (İzmir'den Roma'ya aynı takım).
        Kategori ve sonuç ise etap bazındadır.
      </Text>

      <Space wrap style={{ margin: '12px 0' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setTeamModal({ open: true, record: null }); form.resetFields(); }}
        >
          Yeni Takım
        </Button>
        <Button
          icon={<LinkOutlined />}
          disabled={!attachable.length}
          onClick={() => { setAttachOpen(true); attachForm.resetFields(); }}
        >
          Var Olan Takımı Ekle{attachable.length ? ` (${attachable.length})` : ''}
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={stageTeams}
        size="small"
        scroll={{ x: 1050 }}
        pagination={false}
        locale={{ emptyText: <Empty description="Bu etaba henüz takım eklenmemiş." /> }}
      />

      <Modal
        title={teamModal.record ? 'Takımı Düzenle' : 'Yeni Takım'}
        open={teamModal.open}
        onOk={submitTeam}
        onCancel={() => { setTeamModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Takım Adı" rules={[{ required: true, message: 'Ad girin.' }]}>
            <Input placeholder="Otonom Çiftçiler" />
          </Form.Item>
          <Form.Item name="competition_project_id" label="Proje">
            <Select
              allowClear
              placeholder="Proje havuzundan seçin"
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <Form.Item name="robot_name" label="Robot Adı">
            <Input />
          </Form.Item>
          <Form.Item name="robot_cost" label="Robot Maliyeti (₺)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Var Olan Takımı Bu Etaba Ekle"
        open={attachOpen}
        onOk={submitAttach}
        onCancel={() => { setAttachOpen(false); attachForm.resetFields(); }}
        okText="Ekle"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={attachForm} layout="vertical">
          <Form.Item
            name="competition_team_id"
            label="Takım"
            rules={[{ required: true, message: 'Takım seçin.' }]}
          >
            <Select options={attachable.map((t) => ({ value: t.id, label: t.name }))} />
          </Form.Item>
          <Form.Item name="category" label="Kategori">
            <Input placeholder="Yeşil Girişimcilik…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StageTeamsTab;
